import produce from 'immer';

import State from '../core/State';
import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PerformanceReport,
  PerformanceReportSummaryRequest,
  PeriodicDetailPerformanceWithAllFieldsFragment,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
} from './../generated/graphql';

export const setHasLocalProject = (hasLocalData: boolean) => (state: State): State => ({
  ...state,
  project: {
    ...state.project,
    hasLocalData,
  },
});

export const setPerformanceReports = (
  reports: Map<string, { report: PerformanceReport; request: PerformanceReportSummaryRequest }>,
) => (state: State): State =>
  produce(state, (draft) => {
    const performanceReports = new Map();
    reports.forEach((value, key) => {
      performanceReports.set(key, {
        ...value,
        dailySummaryPerformanceFetching: 0,
        dailySummaryPerformance: null,
        periodicDetailPerformanceFetching: 0,
        periodicDetailPerformance: null,
        periodicImprovementPerformanceFetching: 0,
        periodicImprovementPerformance: null,
      });
    });
    draft.projectReporting.performanceReports = performanceReports;
  });

export const setReportingRequest = (report: PerformanceReport, request: PerformanceReportSummaryRequest) => (
  state: State,
): State =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      request,
    });
  });

export const startFetchingDailySummaryPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      dailySummaryPerformanceFetching: value.dailySummaryPerformanceFetching + 1,
    });
  });

export const setDailySummaryPerformance = (
  report: PerformanceReport,
  performance: DailySummaryPerformanceWithAllFieldsFragment[],
) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      dailySummaryPerformanceFetching: value.dailySummaryPerformanceFetching - 1,
      dailySummaryPerformance: performance,
    });
  });

export const finishFetchingDailySummaryPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      dailySummaryPerformanceFetching: value.dailySummaryPerformanceFetching - 1,
    });
  });

export const startFetchingPeriodicDetailPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicDetailPerformanceFetching: value.periodicDetailPerformanceFetching + 1,
    });
  });

export const setPeriodicDetailPerformance = (
  report: PerformanceReport,
  performance: PeriodicDetailPerformanceWithAllFieldsFragment,
) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicDetailPerformanceFetching: value.periodicDetailPerformanceFetching - 1,
      periodicDetailPerformance: performance,
    });
  });

export const finishFetchingPeriodicDetailPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicDetailPerformanceFetching: value.periodicDetailPerformanceFetching - 1,
    });
  });

export const startFetchingPeriodicImprovementPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicImprovementPerformanceFetching: value.periodicImprovementPerformanceFetching + 1,
    });
  });

export const setPeriodicImprovementPerformance = (
  report: PerformanceReport,
  performance: PeriodicImprovementPerformanceWithAllFieldsFragment,
) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicImprovementPerformanceFetching: value.periodicImprovementPerformanceFetching - 1,
      periodicImprovementPerformance: performance,
    });
  });

export const finishFetchingPeriodicImprovementPerformance = (report: PerformanceReport) => (state: State) =>
  produce(state, (draft) => {
    const value = draft.projectReporting.performanceReports.get(report.id)!;
    draft.projectReporting.performanceReports.set(report.id, {
      ...value,
      periodicImprovementPerformanceFetching: value.periodicImprovementPerformanceFetching - 1,
    });
  });
